import type { MenuKey } from './menus';

/**
 * The portal's navigation has two levels: the vertical rail on the left picks a
 * section, and each section swaps in its own row of tabs.
 *
 * Which sections and tabs exist, what they are called and in what order is a
 * layout the superadmin edits (pages/LayoutAdmin). What a tab can SHOW is not
 * editable: it is one of the screens below, which are code.
 */

interface ScreenMeta {
  /** Default name for a tab that shows this screen. */
  label: string;
  /** The permission that opens it; see lib/menus. */
  menu: MenuKey;
  /** Whether the status strip sits above it. Documents and Presentation fill the workspace. */
  strip: boolean;
  /** Wired to the live feed; the rest show N/A in the strip. */
  live: boolean;
}

export const SCREENS = {
  dashboard: { label: 'Dashboard', menu: 'overview', strip: true, live: true },
  'operations-update': { label: 'Operations Update', menu: 'overview', strip: true, live: false },
  presentation: { label: 'Presentation', menu: 'overview', strip: false, live: false },
  'well-progress': { label: 'Well Progress', menu: 'overview', strip: true, live: false },
  'well-diagram': { label: 'Well Diagram', menu: 'overview', strip: true, live: false },
  'well-prognosis': { label: 'Well Prognosis', menu: 'overview', strip: true, live: false },
  'rt-time-log': { label: 'RT Time Log', menu: 'rt-logs', strip: true, live: true },
  'rt-time-depth': { label: 'RT Time-Depth', menu: 'rt-logs', strip: true, live: false },
  'rt-depth-log': { label: 'RT Depth Log', menu: 'rt-logs', strip: true, live: true },
  'composite-log': { label: 'Composite Log', menu: 'rt-logs', strip: true, live: true },
  'pressure-log': { label: 'Pressure Log', menu: 'rt-logs', strip: true, live: true },
  'rt-pore-pressure': { label: 'RT Pore Pressure', menu: 'rt-logs', strip: true, live: true },
  documents: { label: 'Documents', menu: 'documents', strip: false, live: false },
} as const satisfies Record<string, ScreenMeta>;

export type ScreenKey = keyof typeof SCREENS;

export function isScreenKey(value: unknown): value is ScreenKey {
  return typeof value === 'string' && Object.hasOwn(SCREENS, value);
}

export interface NavTab {
  /** Stable; this is what the URL carries, so renaming a tab breaks no link. */
  id: string;
  label: string;
  screen: ScreenKey;
  hidden: boolean;
}

export interface NavRail {
  id: string;
  label: string;
  /** The square under the label, #rrggbb. */
  color: string;
  hidden: boolean;
  tabs: NavTab[];
}

export interface NavLayout {
  rails: NavRail[];
}

export const LABEL_MAX = 40;

function tab(screen: ScreenKey): NavTab {
  // The default ids are the names the URL has always used, so old links and
  // bookmarks (?rail=RT Logs&tab=Composite Log) still land where they did.
  return { id: SCREENS[screen].label, label: SCREENS[screen].label, screen, hidden: false };
}

/** What the portal looks like until a superadmin says otherwise. */
export const DEFAULT_LAYOUT: NavLayout = {
  rails: [
    {
      id: 'Overview',
      label: 'Overview',
      color: '#d03b34',
      hidden: false,
      tabs: [
        tab('dashboard'),
        tab('operations-update'),
        tab('presentation'),
        tab('well-progress'),
        tab('well-diagram'),
        tab('well-prognosis'),
      ],
    },
    {
      id: 'RT Logs',
      label: 'RT Logs',
      color: '#e58a1a',
      hidden: false,
      tabs: [
        tab('rt-time-log'),
        tab('rt-time-depth'),
        tab('rt-depth-log'),
        tab('composite-log'),
        tab('pressure-log'),
        tab('rt-pore-pressure'),
      ],
    },
    {
      id: 'Documents',
      label: 'Documents',
      color: '#2e9e4f',
      hidden: false,
      // A section with a single tab shows no tab row, which is how Documents
      // has always looked.
      tabs: [tab('documents')],
    },
  ],
};

export function newId(prefix: 'rail' | 'tab'): string {
  return `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

const text = (value: unknown) => (typeof value === 'string' ? value.trim().slice(0, LABEL_MAX) : '');

/**
 * Turns whatever was stored into something safe to render, or null when there
 * is nothing usable in it. Stored data is not trusted: it may be from an older
 * version, hand-edited, or name a screen that no longer exists. Ids must be
 * unique because the URL and React both key on them — sections among
 * themselves, tabs within their section. (A tab may share its section's id:
 * the default Documents section holds a Documents tab.)
 */
export function sanitiseLayout(raw: unknown): NavLayout | null {
  const source = (raw as { rails?: unknown } | null)?.rails;
  if (!Array.isArray(source)) return null;

  const fresh = (seen: Set<string>, id: unknown) => {
    if (typeof id !== 'string' || id === '' || seen.has(id)) return false;
    seen.add(id);
    return true;
  };

  const railIds = new Set<string>();
  const rails: NavRail[] = [];
  for (const r of source as Partial<NavRail>[]) {
    if (!r || !fresh(railIds, r.id)) continue;

    const tabIds = new Set<string>();
    const tabs: NavTab[] = [];
    for (const t of Array.isArray(r.tabs) ? (r.tabs as Partial<NavTab>[]) : []) {
      if (!t || !isScreenKey(t.screen) || !fresh(tabIds, t.id)) continue;
      tabs.push({ id: t.id as string, label: text(t.label), screen: t.screen, hidden: t.hidden === true });
    }

    rails.push({
      id: r.id as string,
      label: text(r.label),
      color: /^#[0-9a-f]{6}$/i.test(String(r.color)) ? String(r.color) : '#5d6d78',
      hidden: r.hidden === true,
      tabs,
    });
  }

  return rails.length > 0 ? { rails } : null;
}

/** What is wrong with a layout, in words for the person editing it. Empty means fine. */
export function validateLayout(layout: NavLayout): string[] {
  const problems: string[] = [];
  const railNames = new Set<string>();

  layout.rails.forEach((rail, index) => {
    const name = rail.label.trim();
    if (!name) problems.push(`Section ${index + 1} needs a name.`);
    else if (railNames.has(name.toLowerCase())) problems.push(`Two sections are both called “${name}”.`);
    railNames.add(name.toLowerCase());

    const shown = name || `Section ${index + 1}`;
    if (rail.tabs.length === 0) {
      problems.push(`“${shown}” has no tabs. Add one, or delete the section.`);
    }

    const tabNames = new Set<string>();
    for (const t of rail.tabs) {
      const tabName = t.label.trim();
      if (!tabName) problems.push(`A tab in “${shown}” needs a name.`);
      else if (tabNames.has(tabName.toLowerCase())) {
        problems.push(`“${shown}” has two tabs called “${tabName}”.`);
      }
      tabNames.add(tabName.toLowerCase());
    }
  });

  const anythingVisible = layout.rails.some((r) => !r.hidden && r.tabs.some((t) => !t.hidden));
  if (!anythingVisible) problems.push('At least one section with one tab has to stay visible.');

  return problems;
}

/**
 * The layout as one person sees it: hidden items gone, tabs they may not open
 * gone, and sections left with nothing in them gone too. Permission follows
 * the screen, not the section, so moving a log into another section does not
 * hand it to someone who was never given RT Logs.
 */
export function visibleRails(layout: NavLayout, mayOpen: (menu: MenuKey) => boolean): NavRail[] {
  return layout.rails
    .filter((rail) => !rail.hidden)
    .map((rail) => ({
      ...rail,
      tabs: rail.tabs.filter((t) => !t.hidden && mayOpen(SCREENS[t.screen].menu)),
    }))
    .filter((rail) => rail.tabs.length > 0);
}

/**
 * Read a section out of the URL. Anything unrecognised — a typo, a link to a
 * section since deleted — falls back to the first one rather than leaving the
 * shell with nothing to render.
 */
export function pickRail(rails: NavRail[], id: string | null): NavRail | undefined {
  return rails.find((rail) => rail.id === id) ?? rails[0];
}

/** Read a tab out of the URL, checked against the section it arrived with. */
export function pickTab(rail: NavRail, id: string | null): NavTab | undefined {
  return rail.tabs.find((t) => t.id === id) ?? rail.tabs[0];
}

/** Where a screen lives for this person, if anywhere — for links that jump to it. */
export function locateScreen(
  rails: NavRail[],
  screen: ScreenKey,
): { rail: string; tab: string | null } | null {
  for (const rail of rails) {
    const hit = rail.tabs.find((t) => t.screen === screen);
    // A lone tab is not written into the URL; see Dashboard.selectRail.
    if (hit) return { rail: rail.id, tab: rail.tabs.length > 1 ? hit.id : null };
  }
  return null;
}
